import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AbstractMerchantSupplierComponent } from 'app/core/common/abstract.merchant-supplier.component';
import { Address } from 'app/core/models/address';
import { Country } from 'app/core/models/country';
import { Items } from 'app/core/models/items';
import { Merchant } from 'app/core/models/merchant';
import { PointOfService } from 'app/core/models/pointOfService';
import { Supplier } from 'app/core/models/supplier';
import { MerchantService } from 'app/core/services/merchant.service';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { PosService } from 'app/core/services/pos.service';
import { SupplierService } from 'app/core/services/supplier.service';
import { TransactionService } from 'app/core/services/transaction.service';
import { NGXLogger } from 'ngx-logger';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AuthenticatedRouteGuard } from 'security/authenticatedRouteGuard.service';

@Component({
  selector: 'app-pos-crud',
  templateUrl: './pos-crud.component.html'
})
export class PosCrudComponent extends AbstractMerchantSupplierComponent implements OnInit, OnDestroy  {

  isCreateMode: boolean;
  isEditMode: boolean;
  isDetailsMode: boolean;

  isLoading: boolean = true;

  pos: PointOfService;
  posId: any;

  posForm: FormGroup;
  productForm: FormGroup;
  subSave: Subscription;
  sub: Subscription;
  isSaveNeeded: boolean;

  posData1: Items<PointOfService>;
  controlProductArray;
  countryItems: Array<Country> = Array<Country>();
  posTypeItems: Array<String> = new Array<String>();
  posDataProviderItems: Array<String> = new Array<String>();

  paymentMeansList = [];
  currenciesList = [];

  transactions = [];
  isIdTechnicalProvider : boolean;
  supplier : any;

  merchants: Array<Merchant>;
  suppliers: Array<Supplier>;

  constructor(
    protected log: NGXLogger,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    protected posService: PosService,
    private roleGuard: AuthenticatedRouteGuard,
    protected supplierService: SupplierService,
    public router: Router,
    private notifService: NotifMessageService,
    private translateService: TranslateService,
    private transactionService: TransactionService,
    protected merchantService: MerchantService
  ) { super(supplierService, merchantService, posService, log, router); }


  ngOnInit(): void {
    this.posTypeItems = this.getPosType();
    this.posDataProviderItems = this.getPosDataProvider();
    this.paymentMeansList = this.getPaymentMeans();
    this.currenciesList = this.getCurrencies();
    this.countryItems = this.getCountrys();

    if (this.roleGuard.isEnabled("SUPPLIER_READ")) {
      this.getSuppliers();
    }
    if (this.roleGuard.isEnabled("MERCHANT_READ")) {
      this.getMerchants();
    }

    this.posId = this.route.snapshot.params['id'];
    this.isCreateMode = !this.posId;

    if(this.isCreateMode){
      this.isLoading = false;
      this.initForm(this.isCreateMode);
    } else {
        this.posService.getPosById(this.posId).subscribe(pos => {
          this.pos = pos;
          this.isLoading = false;

          if(this.router.url.startsWith("/service/pos/edit")) {
            this.isEditMode = true;
            this.initForm(this.isCreateMode);
            this.isSaveNeeded = this.supplierService.isSaveNeeded = false;
            this.posService.saveSubject.subscribe(isSaveNeededTab => {
              this.isSaveNeeded = isSaveNeededTab[0];
            });
          } else {
            if (this.roleGuard.isEnabled("SUPPLIER_READ") && this.pos.supplierId !== null) {
              this.supplierService.getSupplierById(this.pos.supplierId).subscribe(supp => {
                this.isDetailsMode = true;
                this.supplier = supp;
                this.isIdTechnicalProvider = this.isMerchantWalletProvider();
                if(!this.isIdTechnicalProvider) {
                  this.getTransactions();
                }
              });
            }
          }
        });
    }
  }

  ngOnDestroy() : void {
    if (this.subSave) {
      this.subSave.unsubscribe();
    }
  }

  get posControl() {
    return this.posForm.controls;
  }

  onSelectChange() : void{
    this.onPosDataProviderChange();
  }

  initForm(isCreateMode : boolean) : void{
    this.posForm = this.formBuilder.group({
      name: new FormControl(!isCreateMode ? this.pos.name : '', Validators.required),
      vatReference: new FormControl(!isCreateMode ? this.pos.vatReference : ''),
      description: new FormControl(!isCreateMode ? this.pos.description : ''),
      supplierId: new FormControl(!isCreateMode ? this.pos.supplierId : '', Validators.required),
      posDataProvider: new FormControl(!isCreateMode ? this.pos.posDataProvider : 'MERCHANT_WALLET', Validators.required),
      posExternalRef: new FormControl(!isCreateMode ? this.pos.externalRef : '', [Validators.required], [this.checkExternalRefDuplication()]),
      idTechnicalProvider: new FormControl(!isCreateMode ? this.pos.idTechnicalProvider: ''),
      country: new FormControl(!isCreateMode && this.pos.address && this.pos.address.country ? this.pos.address.country : '', Validators.required),
      paymentMeans: new FormControl(!isCreateMode ? this.pos.paymentMeans : '', Validators.required),
      city: new FormControl(!isCreateMode && this.pos.address && this.pos.address.city ? this.pos.address.city : '', Validators.required),
      type: new FormControl(!isCreateMode ? this.pos.type : '', Validators.required),
      line1: new FormControl(!isCreateMode && this.pos.address && this.pos.address.address_line1 ? this.pos.address.address_line1 : '', Validators.required),
      line2: new FormControl(!isCreateMode && this.pos.address && this.pos.address.address_line2 ? this.pos.address.address_line2 : ''),
      line3: new FormControl(!isCreateMode && this.pos.address && this.pos.address.address_line3 ? this.pos.address.address_line3 : ''),
      longitude: new FormControl(!isCreateMode && this.pos.geographicCoordinates && this.pos.geographicCoordinates.longitude ? this.pos.geographicCoordinates.longitude : '', Validators.compose([
        Validators.required,
        Validators.pattern(this.longitudeRegex)
      ])),
      latitude: new FormControl(!isCreateMode && this.pos.geographicCoordinates && this.pos.geographicCoordinates.latitude ? this.pos.geographicCoordinates.latitude : '', Validators.compose([
        Validators.required,
        Validators.pattern(this.latitudeRegex)
      ])),
      zip: new FormControl(!isCreateMode && this.pos.address && this.pos.address.zip_code ? this.pos.address.zip_code : null, Validators.compose([
        Validators.required,
        Validators.pattern(this.zipRegex)
      ])),
      currency: new FormControl(!isCreateMode ? this.pos.currency : this.currenciesList[0], Validators.required)
    });
    if (!isCreateMode) {
      this.subSave = this.posForm.valueChanges.subscribe(() => {
        this.posService.isSaveNeeded = true;
        this.posService.saveSubject.next([this.posService.isSaveNeeded]);
      });
    }
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.isSaveNeeded) {
      this.notifService.emitModal("SAVE_NEEDED");
      return this.notifService.modalResponseSubject$;
    } else {
      return true;
    }
  }

  save() : void {
    let newPos = Object.assign({}, this.pos);
    newPos.id = this.pos.id;
    newPos.paymentMeans = this.posForm.get('paymentMeans').value;
    newPos.name = this.posForm.get('name').value;
    newPos.vatReference = this.posForm.get('vatReference').value;
    newPos.description = this.posForm.get('description').value;
    newPos.posDataProvider = this.posForm.get('posDataProvider').value;
    newPos.externalRef = this.posForm.get('posExternalRef').value;
    if (newPos.posDataProvider !== "MERCHANT_WALLET") {
      newPos.idTechnicalProvider = this.posForm.get('idTechnicalProvider').value;
    }
    newPos.geographicCoordinates = {
      latitude: this.posForm.get('latitude').value,
      longitude: this.posForm.get('longitude').value
    };
    let posAddress = new Address();
    posAddress = {
      address_line1: this.posForm.get('line1').value,
      address_line2: this.posForm.get('line2').value,
      address_line3: this.posForm.get('line3').value,
      zip_code: this.posForm.get('zip').value,
      city: this.posForm.get('city').value.toUpperCase(),
      country: this.posForm.get('country').value
    };

    newPos.address = posAddress;
    newPos.type = this.posForm.get('type').value;
    newPos.supplierId = this.posForm.get('supplierId').value;
    newPos.currency = this.posForm.get('currency').value;

    this.posService.updatePos(newPos).subscribe(() => {
      this.posService.isSaveNeeded = false;
      this.posService.saveSubject.next([this.posService.isSaveNeeded]);
      this.notifService.notifySuccess('SAVED');
    });
  }

  onSubmitForm() : void{
    let newPos = new PointOfService();
    newPos.paymentMeans = [];
    newPos.name = this.posForm.get('name').value;
    newPos.vatReference = this.posForm.get('vatReference').value;
    newPos.description = this.posForm.get('description').value;
    newPos.posDataProvider = this.posForm.get('posDataProvider').value;
    newPos.externalRef = this.posForm.get('posExternalRef').value;
    if (newPos.posDataProvider !== "MERCHANT_WALLET") {
      newPos.idTechnicalProvider = this.posForm.get('idTechnicalProvider').value;
    }

    newPos.geographicCoordinates = {
      latitude: this.posForm.get('latitude').value,
      longitude: this.posForm.get('longitude').value
    };

    let posAddress = new Address();
    posAddress = {
      address_line1: this.posForm.get('line1').value,
      address_line2: this.posForm.get('line2').value,
      address_line3: this.posForm.get('line3').value,
      zip_code: this.posForm.get('zip').value,
      city: this.posForm.get('city').value.toUpperCase(),
      country: this.posForm.get('country').value
    };

    newPos.address = posAddress;
    newPos.type = this.posForm.get('type').value;
    newPos.supplierId = this.posForm.get('supplierId').value;
    newPos.paymentMeans = this.posForm.get('paymentMeans').value;
    newPos.currency = this.posForm.get('currency').value;

    this.posService.createPosAPI(newPos).subscribe((pos) => {
      this.router.navigate(['/service/pos']);
      this.notifService.notifySuccess('POS_CREATED');
    });
  }

  checkExternalRefDuplication() : AsyncValidatorFn {
    return (control: AbstractControl) : Observable<ValidationErrors> | null => {
      return  this.posService.findByCriteria(control.value, {}, true).pipe(map(posList => {
          if (this.pos) {
            return posList.content.length > 0 &&
              posList.content.filter(pos => pos.externalRef === control.value).length > 0
              && this.pos.externalRef !== control.value ? {exists:true} : null;
          }
          return posList.content.length > 0 &&
            posList.content.filter(pos => pos.externalRef === control.value).length > 0 ? {exists:true} : null;
        }));
    }
  }

  back() : void {
    this.router.navigate(["/service/pos"]);
  }

  transSearchFnCountry = (term: string, item: any) => {
    term = term.toLocaleLowerCase();
    return this.translateService.instant(item.countryLabel).toLocaleLowerCase().indexOf(term) > -1;
  }

  transSearchFn = (term: string, item: any) => {
    term = term.toLocaleLowerCase();
    return this.translateService.instant(item).toLocaleLowerCase().indexOf(term) > -1;
  }

  isMerchantWalletProvider() : boolean{
    if(this.isDetailsMode) {
      return this.pos.posDataProvider === "MERCHANT_WALLET"
    }
    return this.posForm.get('posDataProvider').value === "MERCHANT_WALLET";
  }

  onPosDataProviderChange() : void {
    if (this.isMerchantWalletProvider()) {
      this.posForm.get('idTechnicalProvider').clearValidators();
    }
    else {
      this.posForm.get('idTechnicalProvider').setValidators(Validators.required);
    }
    this.posForm.get('idTechnicalProvider').updateValueAndValidity();
  }

  getTransactions(){
    let transactionCriteria = { posId: this.posId };
    let transactionPager = { size: 20, sort: "lastModifiedDate,DESC", page: 0 };
    this.transactionService.findByCriteria(transactionCriteria, transactionPager).subscribe(
      transactions => {
        this.transactions = transactions.content;
        this.isLoading = false
      });
  }

  edit(): void {
    this.router.navigate(['/service/pos/edit', this.pos.id]);
  }

}
